import Button from "@app/components/button";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { addToast } from "@app/components/toast/toast.service";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import BannerService from "@app/services/http/banner.service";
import BookService from "@app/services/http/book.service";
import CategoryService from "@app/services/http/category.service";
import { CreateBannerForm, LabelValue } from "@app/types";
import { createBannerValidationSchema } from "@app/validations";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { Form, Formik, FormikContextType } from "formik";
import { ChangeEvent, createRef, useCallback, useRef } from "react";

const CreateBannerPage = () => {
  const { abortController } = useCancellationRequest();
  const inputFileRef = useRef<HTMLInputElement>(null);

  const formRef = createRef<FormikContextType<CreateBannerForm>>();

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
  };

  const onSubmit = (values: CreateBannerForm) => {
    if (!formRef.current) return;

    if (!inputFileRef.current?.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);

      return;
    }

    const file = inputFileRef.current.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const banner = {
      name: values.name,
      thumbnail: file,
      link: values.link,
      bookId: values.book?.value,
      categoryId: values.category?.value,
    };

    BannerService.create(banner).then(() => {
      addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
    });
  };

  const handleResetForm = () => {
    formRef.current?.resetForm();

    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const getBooksOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BookService.getList(
        {
          keyword: inputValue.trim(),
        },
        abortController.signal
      ).then((res) => {
        const data: LabelValue[] = res.data.items
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) => {
            if (inputValue.trim()) {
              return (
                item.label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .indexOf(
                    inputValue
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  ) >= 0
              );
            }

            return true;
          });

        callback(data);
      });
    },
    []
  );

  const getCategoriesOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      CategoryService.getList(
        {
          keyword: inputValue.trim(),
        },
        abortController.signal
      ).then((res) => {
        const data: LabelValue[] = res.data.items
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) => {
            if (inputValue.trim()) {
              return (
                item.label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .indexOf(
                    inputValue
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  ) >= 0
              );
            }

            return true;
          });

        callback(data);
      });
    },
    []
  );

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center ">
        <div className="text-[#343C6A]">Danh sách banner / Tạo mới</div>
      </div>

      <Formik
        innerRef={formRef}
        initialValues={{
          name: "",
          filename: "",
          link: "",
          book: null,
          category: null,
        }}
        onSubmit={onSubmit}
        validationSchema={createBannerValidationSchema}
        validateOnChange
        validateOnBlur
      >
        <Form>
          <div className="mt-6 rounded-xl bg-white py-6 px-5">
            <div className="w-[100%] max-w-[700px]">
              <FormControl name="name">
                <Input className="mt-7" placeholder="Tên banner" />
              </FormControl>
              <FormControl name="filename">
                <Input
                  className="mt-7"
                  inputClassName="!pl-4 pr-[50px]"
                  placeholder="File ảnh"
                  disabled
                  inputIcon={Images.LinkIcon.default}
                  inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                  onIconClick={onAddFileClick}
                />
              </FormControl>
              <input
                ref={inputFileRef}
                type="file"
                name="file"
                hidden
                onChange={handleFileChange}
              />
              <FormControl name="link">
                <Input className="mt-7" placeholder="Link" />
              </FormControl>
              <FormControl name="book">
                <CustomAsyncSelect
                  className="mt-7 rounded-lg"
                  promiseOptions={getBooksOptions}
                  placeholder="Book name"
                  styles={{
                    stylesControl: {
                      minHeight: "52px",
                    },
                  }}
                />
              </FormControl>
              <FormControl name="category">
                <CustomAsyncSelect
                  className="mt-7 rounded-lg"
                  promiseOptions={getCategoriesOptions}
                  placeholder="Category name"
                  styles={{
                    stylesControl: {
                      minHeight: "52px",
                    },
                  }}
                />
              </FormControl>
            </div>
          </div>
          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Tạo"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={handleResetForm}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default CreateBannerPage;
